package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.data.remote.isPermissionDenied
import com.evoapps.evoshift.data.dto.auth.InviteDto
import com.evoapps.evoshift.data.dto.auth.InviteStatus
import com.evoapps.evoshift.data.dto.auth.SensitiveGeneralDto
import com.evoapps.evoshift.data.dto.auth.UserDto
import com.evoapps.evoshift.data.mapper.UserMapper
import com.evoapps.evoshift.domain.provider.SignupSourceProvider
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.data.remote.IdTokenClaims
import com.evoapps.evoshift.data.session.LocalCache
import com.evoapps.evoshift.data.session.SessionEnd
import com.evoapps.evoshift.domain.model.InviteCodes
import com.evoapps.evoshift.domain.model.InviteDetails
import com.evoapps.evoshift.domain.model.LegalDocuments
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.provider.AppleSignIn
import com.evoapps.evoshift.domain.provider.DeviceInfoProvider
import com.evoapps.evoshift.domain.provider.GoogleCredential
import com.evoapps.evoshift.domain.repository.AuthRepository
import com.evoapps.evoshift.domain.usecase.auth.OwnsWorkplacesException
import dev.gitlive.firebase.auth.FirebaseAuth
import dev.gitlive.firebase.auth.FirebaseUser
import dev.gitlive.firebase.auth.GoogleAuthProvider
import dev.gitlive.firebase.auth.OAuthProvider
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import com.evoapps.evoshift.domain.util.AppClock.now
import kotlin.time.Instant

/** Firebase Auth's provider id for Sign in with Apple. */
private const val APPLE_PROVIDER_ID = "apple.com"

/** How long signing up waits at most for the sign-up source to be recorded (SH-240). */
private const val SIGNUP_SOURCE_TIMEOUT_MS = 5_000L

class AuthRepositoryImpl(
    private val auth: FirebaseAuth,
    private val firestore: AppFirestore,
    private val userMapper: UserMapper,
    private val functions: CloudFunctionsClient,
    private val deviceInfo: DeviceInfoProvider,
    /** The Firestore offline cache, cleared when the session ends (SH-177). */
    localCache: LocalCache,
    /** SH-240: the campaign labels the app was opened with, sent once with the sign-up. */
    private val signupSource: SignupSourceProvider = SignupSourceProvider.None
) : AuthRepository {

    private val sessionEnd = SessionEnd({ auth.signOut() }, localCache)

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun getCurrentUser(): Flow<User?> {
        return observeCurrentUser()
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun observeCurrentUser(): Flow<User?> {
        // authStateChanged also fires on token refreshes for the same person: restarting the
        // profile listener then re-emitted the same user and every screen reloaded.
        // A user with no uid yet (the Mac app's custom-token sign-in, until its first token refresh;
        // firebase-java-sdk 0.6.3 reads no localId from verifyCustomToken) is not signed in yet:
        // `users/` with an empty id is an invalid document reference.
        return auth.authStateChanged.map { it?.takeIf { user -> user.uid.isNotBlank() } }
            .distinctUntilChanged { a, b -> a?.uid == b?.uid }.flatMapLatest { firebaseUser ->
            if (firebaseUser == null) {
                flowOf(null)
            } else {
                firestore.collection("users").document(firebaseUser.uid).safeSnapshots
                    .map { snapshot ->
                        if (snapshot.exists) {
                            try {
                                val dto = snapshot.data<UserDto>()
                                // Mapper łączy UserDto z emailem z Auth
                                userMapper.toDomain(dto, emailFromAuth = firebaseUser.email)
                            } catch (e: Exception) {
                                e.printStackTrace()
                                null
                            }
                        } else {
                            null
                        }
                    }
                    .catch { e ->
                        if (e.isPermissionDenied()) {
                            emit(null)
                        } else {
                            throw e
                        }
                    }
            }
        }.distinctUntilChanged()
    }

    override suspend fun login(email: String, pass: String): Result<User> {
        return try {
            val authResult = auth.signInWithEmailAndPassword(email, pass)
            val firebaseUser = authResult.user ?: throw Exception("Login failed: UID is null")
            // SH-243: one EVo account signs in to EVoShift and EVoLabels (once they share a
            // Firebase project). Someone who registered in EVoLabels has no EVoShift profile yet:
            // their first EVoShift sign-in makes it, as Google and Apple sign-ins always did.
            Result.success(profileOf(firebaseUser, providerName = null))
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun register(email: String, pass: String, displayName: String): Result<User> {
        return try {
            val authResult = auth.createUserWithEmailAndPassword(email, pass)
            val uid = authResult.user?.uid ?: throw Exception("Registration failed: UID is null")
            val profile = createProfile(uid, displayName, email, photoUrl = null)
            // Our own e-mail with a link to evoshift.app (functions/index.js). The account exists
            // either way: if this fails, the verification screen can send it again.
            runCatching { requestVerificationEmail() }
            Result.success(userMapper.toDomain(profile, emailFromAuth = email))
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun signInWithGoogle(credential: GoogleCredential): Result<User> {
        return try {
            val firebaseUser = if (credential.signedIn) {
                // The Mac app's browser flow has signed in already (SH-97).
                auth.currentUser
            } else {
                auth.signInWithCredential(
                    GoogleAuthProvider.credential(credential.idToken, credential.accessToken)
                ).user
            } ?: throw Exception("Google sign-in failed: UID is null")
            Result.success(profileOf(firebaseUser, providerName = null))
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun signInWithApple(signIn: AppleSignIn): Result<User> {
        return try {
            val firebaseUser = when (signIn) {
                // iOS: Apple's ID token plus the nonce whose SHA-256 the sheet was given.
                is AppleSignIn.Credential -> auth.signInWithCredential(
                    OAuthProvider.credential(APPLE_PROVIDER_ID, idToken = signIn.idToken, rawNonce = signIn.rawNonce)
                ).user
                // Android / web / Mac: Firebase's own OAuth flow or the Mac app's browser flow
                // has signed in already.
                is AppleSignIn.SignedIn -> auth.currentUser
            } ?: throw Exception("Apple sign-in failed: UID is null")
            // Apple shares the name only on the first authorization: keep it on the sign-in
            // account too (Firebase does not take it from a native credential).
            val name = signIn.fullName?.trim()?.takeIf { it.isNotEmpty() }
            if (name != null && firebaseUser.displayName.isNullOrBlank()) {
                runCatching { firebaseUser.updateProfile(displayName = name) }
            }
            val user = profileOf(firebaseUser, providerName = name)
            // The server swaps the one-time code for a refresh token, which deleting the account
            // revokes (Apple's rule; functions/appleAuth.js). Best effort: sign-in never fails on it.
            val code = (signIn as? AppleSignIn.Credential)?.authorizationCode
            if (code != null) {
                runCatching {
                    functions.call("storeAppleToken", buildJsonObject {
                        put("authorizationCode", code)
                        put("client", "app") // the native sheet's code is issued for the bundle id
                    })
                }
            }
            Result.success(user)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * The profile of someone signed in, made on their first EVoShift sign-in. With Google or Apple
     * signing in is also how people register: the first time there is no profile yet. The provider
     * has verified the address (with Apple possibly a private-relay one, which mail_queue delivers
     * to like any other), so no verification mail. With an e-mail and password (SH-243) it is an
     * EVo account made in EVoLabels; whether its address is verified is the account's, as for
     * everyone. Invitations are accepted afterwards, as for everyone.
     */
    private suspend fun profileOf(firebaseUser: FirebaseUser, providerName: String?): User {
        val email = firebaseUser.email.orEmpty()
        val snapshot = firestore.collection("users").document(firebaseUser.uid).get()
        val profile = if (snapshot.exists) {
            snapshot.data<UserDto>()
        } else {
            createProfile(
                uid = firebaseUser.uid,
                displayName = providerName
                    ?: firebaseUser.displayName?.takeIf { it.isNotBlank() }
                    ?: email.substringBefore('@'),
                email = email,
                photoUrl = firebaseUser.photoURL
            )
        }
        return userMapper.toDomain(profile, emailFromAuth = firebaseUser.email)
    }

    /** The public profile (no e-mail) and the private 'general' document holding the e-mail. */
    private suspend fun createProfile(uid: String, displayName: String, email: String, photoUrl: String?): UserDto {
        val profile = UserDto(
            uid = uid,
            displayName = displayName,
            engagements = emptyMap(),
            isShadow = false,
            photoUrl = photoUrl,
            createdAt = now().toString()
        )
        val userDoc = firestore.collection("users").document(uid)
        userDoc.set(profile)
        // The sign-up screen says that signing up accepts the terms and the privacy policy: keep
        // which version, and when (SH-50; shown again in the person's data export).
        userDoc.updateFields {
            "termsAcceptedVersion" to LegalDocuments.TERMS_VERSION
            "termsAcceptedAt" to now().toString()
        }
        userDoc.collection("sensitive").document("general").set(
            SensitiveGeneralDto(
                email = email,
                phoneNumber = null,
                address = null,
                bankAccountNumber = null,
                isShadow = false
            )
        )
        recordSignupSource()
        return profile
    }

    /**
     * SH-240: where the new account came from (campaign labels and the platform) for the
     * activation funnel (functions/funnel.js, docs/funnel.md). Best effort and bounded: signing up
     * never waits long for it or fails on it.
     */
    private suspend fun recordSignupSource() {
        runCatching {
            withTimeoutOrNull(SIGNUP_SOURCE_TIMEOUT_MS) {
                val source = runCatching { signupSource.signupSource() }.getOrNull()
                functions.call("recordSignupSource", buildJsonObject {
                    source?.utmSource?.let { put("utmSource", it) }
                    source?.utmCampaign?.let { put("utmCampaign", it) }
                    source?.ref?.let { put("ref", it) }
                    put("platform", deviceInfo.getPlatform().name)
                })
            }
        }
    }

    override suspend fun sendEmailVerification(): Result<Unit> {
        return try {
            requestVerificationEmail()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Account e-mails go out as our own mail linking to evoshift.app (like EVoLabels), not
     * Firebase's default one: functions/index.js generates the link and queues the mail.
     */
    private suspend fun requestVerificationEmail() {
        functions.call("sendVerificationEmail", buildJsonObject { put("language", deviceInfo.getLanguage()) })
    }

    override suspend fun isEmailVerified(): Boolean {
        val user = auth.currentUser ?: return false
        return try {
            user.isEmailVerified
        } catch (e: NotImplementedError) {
            // The desktop app's SDK (firebase-java-sdk, SH-97) has no isEmailVerified: the ID
            // token's claim instead, from a fresh token so an address verified just now counts.
            IdTokenClaims.emailVerified(user.getIdToken(true))
        }
    }

    override suspend fun reloadUser(): Result<Unit> {
        return try {
            auth.currentUser?.reload()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /** Signs out and clears what the session cached on this device (shared tablets, SH-177). */
    override suspend fun signOut() {
        sessionEnd.signOut()
    }

    override suspend fun deleteAccount(): Result<Unit> {
        return try {
            functions.call("deleteAccount", buildJsonObject { put("confirm", "DELETE") })
            // The server deleted EVoShift's data and profile, and the sign-in unless EVoLabels still
            // has a profile for it (SH-243); either way this session ends, with its cache.
            runCatching { auth.signOut() }
            sessionEnd.clearCache()
            Result.success(Unit)
        } catch (e: CloudFunctionsException) {
            Result.failure(if (e.code == "failed-precondition") OwnsWorkplacesException() else e)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun refreshUser() {
        // No-op (Firestore streams updates automatically)
    }

    override suspend fun sendPasswordResetEmail(email: String): Result<Unit> {
        return try {
            // Signed out: the function answers the same for unknown addresses.
            functions.call(
                "sendPasswordResetEmail",
                buildJsonObject {
                    put("email", email.trim())
                    put("language", deviceInfo.getLanguage())
                },
                requireSignIn = false
            )
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    // --- INVITE LOGIC ---

    override suspend fun createInvite(
        workplaceId: String,
        workplaceName: String,
        inviterId: String,
        inviterName: String,
        targetShadowUserId: String,
        email: String?
    ): Result<String> {
        return try {
            val code = InviteCodes.generate()
            val createdAt = now()

            val inviteDto = InviteDto(
                id = code,
                workplaceId = workplaceId,
                workplaceName = workplaceName,
                inviterId = inviterId,
                inviterName = inviterName,
                targetShadowUserId = targetShadowUserId,
                permission = "EMPLOYEE",
                status = InviteStatus.PENDING,
                createdAt = createdAt.toString(),
                expiresAt = (createdAt + InviteCodes.VALID_FOR).toString(),
                email = email?.trim()?.takeIf { it.isNotEmpty() },
                language = deviceInfo.getLanguage()
            )

            firestore.collection("invites").document(code).set(inviteDto)
            Result.success(code)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getInviteDetails(code: String): Result<InviteDetails> {
        return try {
            val inviteSnapshot = firestore.collection("invites").document(code).get()
            if (inviteSnapshot.exists) {
                val inviteDto = inviteSnapshot.data<InviteDto>()
                // Expired as acceptInvite (functions/index.js) sees it, so the join screen does not
                // show a workplace the code can no longer join.
                val expired = runCatching { Instant.parse(inviteDto.expiresAt) < now() }.getOrDefault(false)
                if (inviteDto.status != InviteStatus.PENDING || expired) {
                    return Result.failure(Exception("Zaproszenie wygasło lub zostało już użyte."))
                }
                Result.success(InviteDetails(inviteDto.workplaceName, inviteDto.inviterName))
            } else {
                Result.failure(Exception("Nieprawidłowy kod zaproszenia."))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    /**
     * Accepting an invitation moves the pre-created ("shadow") profile and its rate document onto
     * the signed-in user and marks the invitation as used. The invitee may not write those
     * documents under firestore.rules, so the work is done by the `acceptInvite` Cloud Function
     * (functions/index.js) with admin privileges, after it re-validates the invitation.
     */
    override suspend fun acceptInvite(code: String): Result<Unit> {
        if (auth.currentUser == null) return Result.failure(Exception("Musisz być zalogowany"))
        return try {
            functions.call("acceptInvite", buildJsonObject { put("code", code) })
            Result.success(Unit)
        } catch (e: CloudFunctionsException) {
            Result.failure(
                Exception(
                    when (e.code) {
                        "not-found" -> "Kod nieprawidłowy"
                        "failed-precondition" -> "Zaproszenie zużyte lub wygasło"
                        "already-exists" -> "Już należysz do tego lokalu!"
                        // SH-180: ten wrong codes an hour per account (functions/index.js).
                        "resource-exhausted" -> "Zbyt wiele błędnych kodów. Spróbuj ponownie za godzinę."
                        else -> e.message ?: "Nie udało się przyjąć zaproszenia"
                    },
                    e
                )
            )
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}